/**
 * FLOW-V3 §4, §7 — одноразовый токен привязки гостевого переноса.
 *
 * Зачем он вообще есть. В порядке v3 ссылку принимают у ГОСТЯ: строка переноса
 * заводится без аккаунта, и единственное, что связывает открытую вкладку с этой
 * строкой, — токен из ответа `submit`. Вход уводит человека со страницы (Google
 * и Telegram — вообще через полную перезагрузку), поэтому токен обязан пережить
 * переход: он живёт в `localStorage`, ровно тем же приёмом, что `pool_invite`
 * у приглашения в общую подписку.
 *
 * Правила обращения с ним (SECURITY.md, FLOW-V3 §7):
 *  - в аналитику и в логи он не попадает НИКОГДА: полезная нагрузка счётчика
 *    механики (`tradeinAnalytics.ts`) состоит из события, визита и места клика;
 *  - в адресной строке он бывает только у Mini App (`start_param` → `?claim=`),
 *    и оттуда стирается сразу же — `history.replaceState`, чтобы токен не уехал
 *    ни в закладку, ни в `Referer`, ни в историю браузера;
 *  - привязка одна: после `POST /claim` запись снимается.
 *
 * Зависимостей у модуля нет намеренно: его импортирует `postAuthRoute`, то есть
 * он оказывается в стартовом бандле, и тащить туда Pinia ради трёх строк было
 * бы платой ни за что.
 */

/** Ключ хранилища — тот, что назван в FLOW-V3 §4. */
export const TRADEIN_CLAIM_STORAGE_KEY = 'igra_tradein_claim'

/** Имя параметра, которым токен приезжает из Mini App. */
export const TRADEIN_CLAIM_QUERY_PARAM = 'claim'

/**
 * Хранилище бывает недоступно целиком (приватный режим Safari, iframe с
 * запретом хранилища): обращение к свойству там БРОСАЕТ. Для переноса это не
 * повод ронять страницу — просто токен не переживёт вход, и человек войдёт
 * первым, а перенос начнёт уже вошедшим.
 */
function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function storeTradeInClaim(token: string): void {
  if (!token) return

  try {
    storage()?.setItem(TRADEIN_CLAIM_STORAGE_KEY, token)
  } catch {
    // Переполненное или запрещённое хранилище — молча живём дальше.
  }
}

export function readStoredTradeInClaim(): string | null {
  try {
    const value = storage()?.getItem(TRADEIN_CLAIM_STORAGE_KEY) ?? null

    return value || null
  } catch {
    return null
  }
}

export function clearStoredTradeInClaim(): void {
  try {
    storage()?.removeItem(TRADEIN_CLAIM_STORAGE_KEY)
  } catch {
    // Нечего снимать — нечего и чинить.
  }
}

/**
 * Забирает токен из адреса (`/tradein?claim=…`) и СТИРАЕТ его оттуда.
 *
 * Возвращает `true`, если токен был в адресе. Стирание обязательно и делается
 * в том же тике: адрес с токеном человек скопирует в чат, оставит в истории и
 * отдаст любому счётчику, который читает `location.href`.
 */
export function captureTradeInClaimFromUrl(): boolean {
  if (typeof window === 'undefined') return false

  let token: string | null = null
  try {
    token = new URLSearchParams(window.location.search).get(
      TRADEIN_CLAIM_QUERY_PARAM,
    )
  } catch {
    return false
  }

  if (!token) return false

  storeTradeInClaim(token)

  try {
    const url = new URL(window.location.href)
    url.searchParams.delete(TRADEIN_CLAIM_QUERY_PARAM)
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    )
  } catch {
    // Историю подменить не дали — токен всё равно уже в хранилище.
  }

  return true
}

/**
 * FLOW-V4 §2.1 — адрес СЕССИИ переноса `/tradein/s/{resume}`.
 *
 * В отличие от токена это не секрет-на-один-раз: адрес сам по себе ничего не
 * отдаёт чужому (права проверяет бэкенд), поэтому он живёт и в адресной
 * строке, и в хранилище рядом с токеном — так простой `/tradein` и возврат
 * после входа продолжают тот же перенос. Снимается, когда перенос закончен.
 */
export const TRADEIN_RESUME_STORAGE_KEY = 'igra_tradein_resume'

/** Тот же формат, что проверяет бэкенд: `[A-Za-z0-9_-]`, не длиннее 32. */
const RESUME_ID = /^[A-Za-z0-9_-]{1,32}$/

export function isTradeInResumeId(value: unknown): value is string {
  return typeof value === 'string' && RESUME_ID.test(value)
}

/** Идентификатор сессии из полного адреса `https://…/tradein/s/{id}`. */
export function resumeIdFromUrl(url: string | null | undefined): string | null {
  const match = /\/tradein\/s\/([^/?#]+)/.exec(url ?? '')

  return match && isTradeInResumeId(match[1]) ? match[1] : null
}

export function storeTradeInResume(resume: string): void {
  if (!isTradeInResumeId(resume)) return

  try {
    storage()?.setItem(TRADEIN_RESUME_STORAGE_KEY, resume)
  } catch {
    // Не сохранилось — продолжить можно по адресу из строки браузера.
  }
}

export function readStoredTradeInResume(): string | null {
  try {
    const value = storage()?.getItem(TRADEIN_RESUME_STORAGE_KEY) ?? null

    return isTradeInResumeId(value) ? value : null
  } catch {
    return null
  }
}

export function clearStoredTradeInResume(): void {
  try {
    storage()?.removeItem(TRADEIN_RESUME_STORAGE_KEY)
  } catch {
    // Нечего снимать — нечего и чинить.
  }
}

/** Только страница переноса, без внешних адресов, query и токенов. */
export function isTradeInReturnPath(value: unknown): value is string {
  return value === '/tradein' || (
    typeof value === 'string' &&
    value.startsWith('/tradein/s/') &&
    isTradeInResumeId(value.slice('/tradein/s/'.length))
  )
}

/** Внешний вход запоминает адрес в этой вкладке, однократно и на 15 минут. */
export const TRADEIN_AUTH_RETURN_STORAGE_KEY = 'igra_tradein_auth_return'
export const TRADEIN_AUTH_RETURN_TTL_MS = 15 * 60 * 1000

/** Зовётся при нажатии Google/Яндекса, а не при сохранении или чтении переноса. */
export function storeTradeInAuthReturn(path: string): void {
  try {
    window.sessionStorage.removeItem(TRADEIN_AUTH_RETURN_STORAGE_KEY)
    if (!isTradeInReturnPath(path)) return

    window.sessionStorage.setItem(
      TRADEIN_AUTH_RETURN_STORAGE_KEY,
      JSON.stringify({ path, storedAt: Date.now() }),
    )
  } catch {
    // Хранилище запрещено — продолжить перенос можно по его ссылке.
  }
}

/** Для OAuth-возврата или подтверждения email после Яндекса, не обычного захода. */
export function consumeTradeInAuthReturn(): string | null {
  try {
    const raw = window.sessionStorage.getItem(TRADEIN_AUTH_RETURN_STORAGE_KEY)
    window.sessionStorage.removeItem(TRADEIN_AUTH_RETURN_STORAGE_KEY)
    if (!raw) return null

    const value = JSON.parse(raw) as { path?: unknown; storedAt?: unknown } | null
    if (!value || !isTradeInReturnPath(value.path) || typeof value.storedAt !== 'number') return null

    const age = Date.now() - value.storedAt
    return age >= 0 && age < TRADEIN_AUTH_RETURN_TTL_MS ? value.path : null
  } catch {
    return null
  }
}
